import { useState, useEffect } from 'react'

/**
 * Hook to add a timeout to loading states
 * Prevents UI from being stuck in loading state indefinitely
 *
 * @param initialLoading - Initial loading state
 * @param timeout - Timeout in milliseconds
 * @returns Object with safe loading state
 */
export const useLoadingTimeout = (initialLoading: boolean, timeout = 10000) => {
  const [loading, setLoading] = useState(initialLoading)
  const [hasTimedOut, setHasTimedOut] = useState(false)

  // Update loading state when initialLoading changes
  useEffect(() => {
    setLoading(initialLoading)

    // Reset timeout when loading starts again
    if (initialLoading) {
      setHasTimedOut(false)
    }
  }, [initialLoading])

  // Set a timeout if loading is true
  useEffect(() => {
    let timer: NodeJS.Timeout

    if (loading) {
      timer = setTimeout(() => {
        setHasTimedOut(true)
        setLoading(false)
        console.warn(`Loading state timed out after ${timeout}ms`)
      }, timeout)
    }

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [loading, timeout])

  return {
    loading: loading && !hasTimedOut,
    hasTimedOut
  }
}

export default useLoadingTimeout
